/**
 * Pure helpers for the invite set-password page. Kept out of the component file
 * so it exports only a component (fast refresh) and so main.tsx and App.tsx can
 * use them without importing the page.
 */

export const ACCEPT_INVITE_PATH = '/accept-invite'

/** The invite page's path, tolerant of a trailing slash and of case. */
export function isInviteLinkPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').toLowerCase() === ACCEPT_INVITE_PATH
}

/**
 * Whether Sentry may run in THIS document.
 *
 * Never in one loaded from an invite link. Taking the token off the address bar
 * (below) is not enough: the browser's `PerformanceNavigationTiming.name` keeps
 * the URL the page was *navigated to*, fragment included, and has no API to
 * clear it — verified in Chromium: after `history.replaceState` the location is
 * clean and the navigation entry still reads `…#token=…`. Both Session Replay
 * (performance spans) and browser tracing (child spans) read that entry and
 * upload it. A reader of the Sentry project could redeem the token. So on this
 * page the SDK does not start at all: no replay, no tracing, no error capture.
 * A full page load from anywhere else (the "Continue to sign in" button does
 * one) starts it normally.
 */
export function sentryEnabledFor(pathname: string, dsn: string): boolean {
  return Boolean(dsn) && !isInviteLinkPath(pathname)
}

/** The token lives in the URL fragment, which browsers never send to a server. */
export function readInviteToken(hash: string): string | null {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const token = (params.get('token') ?? '').trim()
  return token || null
}

// The bearer token, held in memory only once it has been taken off the URL.
let heldToken: string | null = null

/**
 * Take the invite token out of the address bar, keep it in memory, and strip
 * the fragment — and do it BEFORE anything reads `location` (see main.tsx).
 *
 * The fragment never reaches a server, but other things read `location.href`:
 * Session Replay records the URL it starts on and the browser SDK puts it on
 * every error event. Once the hash is gone there is nothing for them to read.
 * (The navigation-timing entry is the one thing this cannot clean — that is why
 * Sentry is also switched off for this document, see sentryEnabledFor.) The cost
 * is deliberate: a reload no longer finds the token, so the page asks for the
 * emailed link again — which still works, because merely opening it never
 * spends it.
 *
 * Strips the whole fragment on this path even when no token is in it.
 */
export function captureInviteTokenFromUrl(
  loc: Pick<Location, 'pathname' | 'search' | 'hash'> = window.location,
  hist: Pick<History, 'replaceState'> = window.history,
): void {
  if (!isInviteLinkPath(loc.pathname)) return
  const token = readInviteToken(loc.hash)
  if (token) heldToken = token
  if (loc.hash) hist.replaceState(null, '', loc.pathname + loc.search)
}

export function heldInviteToken(): string | null {
  return heldToken
}

export function clearHeldInviteToken(): void {
  heldToken = null
}

const TOKEN_IN_TEXT = /([#?&]token=)[A-Za-z0-9_-]+/g

/** Redact an invite token from any string (a URL, a breadcrumb field). */
export function scrubInviteToken<T>(value: T): T {
  return (typeof value === 'string' ? value.replace(TOKEN_IN_TEXT, '$1[redacted]') : value) as T
}

/**
 * Defence in depth for the Sentry hooks: even if a URL carrying the token were
 * ever captured, send it redacted. `captureInviteTokenFromUrl` is the actual fix.
 */
export function scrubInviteTokenFromBreadcrumb<B extends { data?: Record<string, unknown> }>(crumb: B): B {
  if (crumb.data) {
    for (const key of ['from', 'to', 'url']) {
      const v = crumb.data[key]
      if (typeof v === 'string') crumb.data[key] = scrubInviteToken(v)
    }
  }
  return crumb
}

export function scrubInviteTokenFromEvent<E extends object>(event: E): E {
  const e = event as {
    request?: { url?: string }
    transaction?: string
    breadcrumbs?: Array<{ data?: Record<string, unknown> }>
  }
  if (e.request?.url) e.request.url = scrubInviteToken(e.request.url)
  if (e.transaction) e.transaction = scrubInviteToken(e.transaction)
  for (const crumb of e.breadcrumbs ?? []) scrubInviteTokenFromBreadcrumb(crumb)
  return event
}

export const PASSWORD_MIN_LENGTH = 12
/** bcrypt reads only the first 72 BYTES; the server refuses anything longer. */
export const PASSWORD_MAX_BYTES = 72

export type PasswordFeedback = {
  message: string
  /** Only length blocks the button; see passwordFeedback. */
  blocking: boolean
}

/**
 * Immediate feedback on a chosen password, mirroring app/core/passwords.py.
 *
 * The server decides. Its policy is Unicode-aware (Python's isupper/islower/
 * isdigit) and counts code points, so a stricter ASCII-only check here would
 * leave the button disabled for a password the server accepts. Hence:
 *  - length counts code points, as `len()` does, and — with the 72-byte cap —
 *    is all that blocks submission (a too-short or too-long password is certain
 *    to be refused);
 *  - the character-class check uses Unicode properties and is ADVISORY: it
 *    explains, but never prevents sending, and the server's answer is shown.
 *    `\p{N}` is deliberately a little wider than Python's isdigit, erring
 *    toward letting the server decide.
 */
export function passwordFeedback(password: string): PasswordFeedback | null {
  if ([...password].length < PASSWORD_MIN_LENGTH) {
    return { message: `Use at least ${PASSWORD_MIN_LENGTH} characters.`, blocking: true }
  }
  if (new TextEncoder().encode(password).length > PASSWORD_MAX_BYTES) {
    return {
      message: `Use at most ${PASSWORD_MAX_BYTES} bytes — accented and non-Latin characters count as 2–4 each.`,
      blocking: true,
    }
  }
  if (!(/\p{Lu}/u.test(password) && /\p{Ll}/u.test(password) && /\p{N}/u.test(password))) {
    return {
      message: 'Passwords need an uppercase letter, a lowercase letter, and a digit — the server makes the final check.',
      blocking: false,
    }
  }
  return null
}
